export const tierOneHeadings = ['h1', 'h2', 'h3']; // headings that are used for the table of contents

export const allowedTags = [
  'address',
  'article',
  'aside',
  'footer',
  'header',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hgroup',
  'main',
  'nav',
  'section',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'hr',
  'li',
  'main',
  'ol',
  'p',
  'pre',
  'ul',
  'a',
  'abbr',
  'b',
  'bdi',
  'bdo',
  'br',
  'cite',
  'code',
  'data',
  'dfn',
  'em',
  'i',
  'kbd',
  'mark',
  'q',
  'rb',
  'rp',
  'rt',
  'rtc',
  'ruby',
  's',
  'samp',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'time',
  'u',
  'var',
  'wbr',
  'caption',
  'col',
  'colgroup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'img',
  'video',
  'source',
  'iframe',
];

export const attributeMap = {
  // Standard HTML attributes requiring camelCase
  acceptcharset: 'acceptCharset',
  accesskey: 'accessKey',
  autocomplete: 'autoComplete',
  autofocus: 'autoFocus',
  autoplay: 'autoPlay', // For <video> and <audio>
  autosave: 'autoSave',
  bgcolor: 'bgColor',
  cellpadding: 'cellPadding',
  cellspacing: 'cellSpacing',
  charset: 'charSet',
  class: 'className',
  classid: 'classID',
  colspan: 'colSpan',
  contenteditable: 'contentEditable',
  contextmenu: 'contextMenu',
  crossorigin: 'crossOrigin',
  datetime: 'dateTime',
  enctype: 'encType',
  for: 'htmlFor',
  formaction: 'formAction',
  formenctype: 'formEncType',
  formmethod: 'formMethod',
  formnovalidate: 'formNoValidate',
  formtarget: 'formTarget',
  frameborder: 'frameBorder',
  hreflang: 'hrefLang',
  inputmode: 'inputMode',
  longdesc: 'longDesc',
  marginheight: 'marginHeight',
  marginwidth: 'marginWidth',
  maxlength: 'maxLength',
  mediagroup: 'mediaGroup',
  minlength: 'minLength',
  novalidate: 'noValidate',
  playsinline: 'playsInline', // For <video> and <audio>
  readonly: 'readOnly',
  rowspan: 'rowSpan',
  spellcheck: 'spellCheck',
  srcdoc: 'srcDoc',
  srclang: 'srcLang',
  srcset: 'srcSet',
  tabindex: 'tabIndex',
  usemap: 'useMap',
  allowfullscreen: 'allowFullScreen',
  allowpaymentrequest: 'allowPaymentRequest',
  allowtransparency: 'allowTransparency',
  allowusermedia: 'allowUserMedia',
  lowsrc: 'lowSrc',
  // SVG-specific attributes requiring camelCase
  clippath: 'clipPath',
  fillopacity: 'fillOpacity',
  fontfamily: 'fontFamily',
  fontsize: 'fontSize',
  fontweight: 'fontWeight',
  gradienttransform: 'gradientTransform',
  gradientunits: 'gradientUnits',
  markerend: 'markerEnd',
  markerheight: 'markerHeight',
  markermid: 'markerMid',
  markerstart: 'markerStart',
  markerunits: 'markerUnits',
  markerwidth: 'markerWidth',
  maskcontentunits: 'maskContentUnits',
  maskunits: 'maskUnits',
  patterncontentunits: 'patternContentUnits',
  patternunits: 'patternUnits',
  preserveaspectratio: 'preserveAspectRatio',
  spreadmethod: 'spreadMethod',
  stopcolor: 'stopColor',
  stopopacity: 'stopOpacity',
  strokeopacity: 'strokeOpacity',
  strokewidth: 'strokeWidth',
  strokelinecap: 'strokeLinecap',
  strokelinejoin: 'strokeLinejoin',
  textanchor: 'textAnchor',
  textdecoration: 'textDecoration',
  viewbox: 'viewBox',
  xlinkactuate: 'xlinkActuate',
  xlinkarcrole: 'xlinkArcrole',
  xlinkhref: 'xlinkHref',
  xlinkrole: 'xlinkRole',
  xlinkshow: 'xlinkShow',
  xlinktitle: 'xlinkTitle',
  xlinktype: 'xlinkType',
  xmlbase: 'xmlBase',
  xmllang: 'xmlLang',
  xmlspace: 'xmlSpace',
};

// Memoize sanitize config to avoid recreation
export const sanitizeConfig = {
  allowedTags,
  allowedAttributes: {
    '*': [
      'class',
      'className',
      'id',
      'style',
      'title',
      'alt',
      'role',
      'data-*',
      'width',
      'height',
      'src',
      'srcset',
      'href',
      'target',
      'rel',
      'type',
      'value',
      'placeholder',
      'name',
      'disabled',
      'readonly',
      'checked',
      'selected',
      'multiple',
      'size',
      'maxlength',
      'minlength',
      'min',
      'max',
      'step',
      'pattern',
      'required',
      'autofocus',
      'autoplay',
      'controls',
      'loop',
      'muted',
      'preload',
      'poster',
      'crossorigin',
      'sizes',
      'loading',
      'decoding',
      'fetchpriority',
      'colspan',
      'rowspan',
      'scope',
    ],
    a: ['href', 'target', 'rel', 'download', 'hreflang', 'type'],
    img: ['src', 'srcset', 'alt', 'width', 'height', 'loading', 'decoding', 'sizes', 'crossorigin'],
    video: ['src', 'poster', 'preload', 'autoplay', 'controls', 'loop', 'muted', 'width', 'height'],
    audio: ['src', 'preload', 'autoplay', 'controls', 'loop', 'muted'],
    source: ['src', 'type', 'media', 'sizes', 'srcset'],
    iframe: ['src', 'width', 'height', 'frameborder', 'allowfullscreen', 'sandbox'],
    table: ['cellpadding', 'cellspacing', 'border'],
    th: ['colspan', 'rowspan', 'scope', 'style', 'width', 'height', 'class', 'className', 'id', 'title', 'data-*'],
    td: ['colspan', 'rowspan', 'style', 'width', 'height', 'class', 'className', 'id', 'title', 'data-*'],
    ol: ['start', 'reversed', 'type'],
    li: ['value'],
    code: ['class', 'className'],
    pre: ['class', 'className'],
    blockquote: ['cite'],
    q: ['cite'],
    time: ['datetime'],
    abbr: ['title'],
    dfn: ['title'],
  },
  allowedStyles: {
    '*': {
      // Block dangerous properties that can execute JavaScript or cause security issues
      'background-image': false, // Can contain javascript: URLs
      'list-style-image': false, // Can contain javascript: URLs
      content: false, // Can be used for data exfiltration
      behavior: false, // IE-specific, can execute scripts
      binding: false, // Mozilla-specific, can bind XBL
      expression: false, // IE-specific, can execute JavaScript
      '-moz-binding': false, // Can execute XBL
      filter: false, // IE filters can sometimes be exploited

      // Allow everything else with very permissive patterns
      // Color properties
      color: [/.*/],
      'background-color': [/.*/],
      'border-color': [/.*/],
      'outline-color': [/.*/],
      'text-decoration-color': [/.*/],

      // Size and spacing properties
      width: [/.*/],
      height: [/.*/],
      'min-width': [/.*/],
      'max-width': [/.*/],
      'min-height': [/.*/],
      'max-height': [/.*/],
      margin: [/.*/],
      'margin-top': [/.*/],
      'margin-right': [/.*/],
      'margin-bottom': [/.*/],
      'margin-left': [/.*/],
      padding: [/.*/],
      'padding-top': [/.*/],
      'padding-right': [/.*/],
      'padding-bottom': [/.*/],
      'padding-left': [/.*/],

      // Layout properties
      display: [/.*/],
      position: [/.*/],
      top: [/.*/],
      right: [/.*/],
      bottom: [/.*/],
      left: [/.*/],
      float: [/.*/],
      clear: [/.*/],
      'z-index': [/.*/],
      overflow: [/.*/],
      'overflow-x': [/.*/],
      'overflow-y': [/.*/],
      visibility: [/.*/],
      opacity: [/.*/],

      // Typography properties
      'font-family': [/.*/],
      'font-size': [/.*/],
      'font-weight': [/.*/],
      'font-style': [/.*/],
      'font-variant': [/.*/],
      'line-height': [/.*/],
      'text-align': [/.*/],
      'text-decoration': [/.*/],
      'text-transform': [/.*/],
      'text-indent': [/.*/],
      'letter-spacing': [/.*/],
      'word-spacing': [/.*/],
      'white-space': [/.*/],

      // Border properties
      border: [/.*/],
      'border-width': [/.*/],
      'border-style': [/.*/],
      'border-top': [/.*/],
      'border-right': [/.*/],
      'border-bottom': [/.*/],
      'border-left': [/.*/],
      'border-radius': [/.*/],
      'border-collapse': [/.*/],
      'border-spacing': [/.*/],
      outline: [/.*/],
      'outline-width': [/.*/],
      'outline-style': [/.*/],

      // Flexbox properties
      flex: [/.*/],
      'flex-direction': [/.*/],
      'flex-wrap': [/.*/],
      'flex-flow': [/.*/],
      'justify-content': [/.*/],
      'align-items': [/.*/],
      'align-content': [/.*/],
      'align-self': [/.*/],
      'flex-grow': [/.*/],
      'flex-shrink': [/.*/],
      'flex-basis': [/.*/],
      order: [/.*/],

      // Grid properties
      grid: [/.*/],
      'grid-template': [/.*/],
      'grid-template-columns': [/.*/],
      'grid-template-rows': [/.*/],
      'grid-template-areas': [/.*/],
      'grid-gap': [/.*/],
      'grid-column-gap': [/.*/],
      'grid-row-gap': [/.*/],
      'grid-column': [/.*/],
      'grid-row': [/.*/],
      'grid-area': [/.*/],

      // Transform and animation (safe ones)
      transform: [/.*/],
      'transform-origin': [/.*/],
      transition: [/.*/],
      'transition-property': [/.*/],
      'transition-duration': [/.*/],
      'transition-timing-function': [/.*/],
      'transition-delay': [/.*/],
      'animation-name': [/.*/],
      'animation-duration': [/.*/],
      'animation-timing-function': [/.*/],
      'animation-delay': [/.*/],
      'animation-iteration-count': [/.*/],
      'animation-direction': [/.*/],
      'animation-fill-mode': [/.*/],
      'animation-play-state': [/.*/],

      // List properties (except list-style-image which is blocked above)
      'list-style': [/.*/],
      'list-style-type': [/.*/],
      'list-style-position': [/.*/],

      // Table properties
      'table-layout': [/.*/],
      'caption-side': [/.*/],
      'empty-cells': [/.*/],

      // Cursor and interaction
      cursor: [/.*/],
      'pointer-events': [/.*/],
      'user-select': [/.*/],

      // Box shadow and other visual effects
      'box-shadow': [/.*/],
      'text-shadow': [/.*/],

      // Catch-all for any other safe properties we might have missed
      // This allows custom properties (CSS variables) and new CSS properties
      '*': [/^(?!.*(?:javascript|expression|behavior|binding|moz-binding|vbscript|data:|@import)).*/i],
    },
  },
  allowVulnerableTags: false,
  // Allow processing of malformed tags to avoid truncation on content with entity-encoded angle brackets
  allowProtocolRelative: true,
  enforceHtmlBoundary: false,
  // Don't discard text content that appears to be malformed tags
  disallowedTagsMode: 'discard',
  // Temporarily disable transformTags as it interferes with rowspan/colspan preservation
  // transformTags: {
  //   '*': (tagName, attribs) => {
  //     const newAttribs = { ...attribs };
  //     Object.keys(newAttribs).forEach(key => {
  //       if (attributeMap[key]) {
  //         newAttribs[attributeMap[key]] = newAttribs[key];
  //         delete newAttribs[key];
  //       }
  //     });
  //     return { tagName, attribs: newAttribs };
  //   },
  // },
};
